import { useQuery } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { memoriesApi } from 'src/Services/API';
import { MemoryDto, MemoryTranscriptStatus } from 'src/Services/API/Memories.api';

export const memoryQueryKey = (assetId: string | undefined, memoryId: string | undefined) =>
  ['memory', assetId, memoryId] as const;

const TRANSCRIPT_POLL_MS = 5_000;
const TRANSCRIPT_POLL_MAX_MS = 2 * 60_000;

/**
 * A memory with its stories. While a story is still being transcribed, the memory is
 * re-fetched every 5 seconds, for 2 minutes at most.
 */
const useFetchMemory = (memoryId?: string) => {
  const assetId = useCurrentAssetId();

  return useQuery<MemoryDto>({
    queryKey: memoryQueryKey(assetId, memoryId),
    queryFn: async () => {
      if (!assetId || !memoryId) throw new Error('No assetId or memoryId provided');
      return memoriesApi.findOne(assetId, memoryId);
    },
    enabled: !!assetId && !!memoryId,
    refetchInterval: memory => {
      const pending = memory?.recordings.filter(
        recording => recording.transcriptStatus === MemoryTranscriptStatus.Pending
      );
      if (!pending?.length) return false;
      const oldest = Math.min(...pending.map(recording => Date.parse(recording.recordedAt)));
      return Date.now() - oldest < TRANSCRIPT_POLL_MAX_MS ? TRANSCRIPT_POLL_MS : false;
    },
  });
};

export default useFetchMemory;
